import { easeInOut, motion } from 'motion/react'
import { Bell, FileText, Lock, Scale, Shield, UserCheck } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Seo } from '~/components/Seo'
import { PageLayout } from '~/lib/components/layouts/PageLayout'

const privacyPoints = [
  {
    icon: Shield,
    title: 'Data Protection',
    description: 'Your data is encrypted and stored securely following industry best practices.',
  },
  {
    icon: Lock,
    title: 'Access Control',
    description: 'Strict authentication and authorization protocols protect your information.',
  },
  {
    icon: FileText,
    title: 'Transparency',
    description: 'Clear documentation of how we collect, process, and use your data.',
  },
  {
    icon: UserCheck,
    title: 'User Rights',
    description: 'Full control over your data with easy access to privacy settings.',
  },
  {
    icon: Scale,
    title: 'Legal Compliance',
    description: 'Adherence to GDPR, CCPA, and other relevant privacy regulations.',
  },
  {
    icon: Bell,
    title: 'Breach Notification',
    description: 'Prompt notification of any security incidents affecting your data.',
  },
]

const floatingAnimation = {
  initial: { y: 0 },
  animate: {
    y: [-12, 12, -12],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const glowAnimation = {
  initial: { opacity: 0.3 },
  animate: {
    opacity: [0.3, 0.6, 0.3],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const Index = () => {
  return (
    <>
      <Seo
        title="Terms of Service | SOTA Navigator"
        description="The terms that govern your use of SOTA Navigator, including your account, your data, acceptable use, billing and termination."
      />
      <PageLayout>
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-[#001871] text-white">
          {/* Enhanced texture overlay */}
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
          <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

          {/* Floating elements */}
          <motion.div
            variants={floatingAnimation}
            initial="initial"
            animate="animate"
            className="absolute left-20 top-40 hidden size-20 rounded-2xl bg-white/20 shadow-lg backdrop-blur-lg lg:block"
          />
          <motion.div
            variants={floatingAnimation}
            initial="initial"
            animate="animate"
            transition={{ delay: 1 }}
            className="absolute bottom-40 right-20 hidden size-16 rounded-full bg-white/20 shadow-lg backdrop-blur-lg lg:block"
          />

          <div className="relative py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="mx-auto max-w-4xl text-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                  className="space-y-8"
                >
                  <motion.div
                    variants={glowAnimation}
                    initial="initial"
                    animate="animate"
                    className="inline-flex items-center rounded-full bg-white/20 px-6 py-2 text-sm font-medium text-white shadow-lg backdrop-blur-xs"
                  >
                    Legal & Privacy
                  </motion.div>

                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                    className="text-5xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-7xl"
                  >
                    Terms & Privacy Policy
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                    className="mx-auto max-w-3xl text-xl text-white/80"
                  >
                    Our commitment to protecting your privacy and ensuring transparent terms of
                    service
                  </motion.p>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy Highlights */}
        <section className="relative bg-white py-24">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-[0.02] mix-blend-soft-light"></div>

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16 text-center"
            >
              <h2 className="mb-4 text-4xl font-bold text-[#001871]">Privacy Commitment</h2>
              <p className="mx-auto max-w-3xl text-xl text-gray-600">
                Your privacy and data security are our top priorities. Here's how we protect your
                information.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {privacyPoints.map((point, index) => (
                <motion.div
                  key={point.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="border border-gray-100 bg-white transition-all duration-300 hover:shadow-xl">
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="mb-4 rounded-xl bg-[#001871]/5 p-4">
                          <point.icon className="size-10 text-[#001871]" />
                        </div>
                        <h3 className="mb-2 text-xl font-semibold text-[#001871]">{point.title}</h3>
                        <p className="text-gray-600">{point.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Terms Section */}
        <section className="relative bg-gray-50 py-24">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-[0.02] mix-blend-soft-light"></div>

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-4xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mb-16"
              >
                <h2 className="mb-8 text-3xl font-bold text-[#001871]">Terms of Service</h2>

                <div className="space-y-8 text-gray-600">
                  <div>
                    <h3 className="mb-4 text-xl font-semibold text-[#001871]">1. Service Usage</h3>
                    <p className="mb-4">
                      By accessing and using SOTA Navigator, you agree to these terms of service.
                      Our service is intended for professional use in medical device research and
                      documentation.
                    </p>
                  </div>

                  <div>
                    <h3 className="mb-4 text-xl font-semibold text-[#001871]">2. User Accounts</h3>
                    <p className="mb-4">
                      You are responsible for maintaining the confidentiality of your account
                      credentials and for all activities under your account. Notify us immediately
                      of any unauthorized use.
                    </p>
                  </div>

                  <div>
                    <h3 className="mb-4 text-xl font-semibold text-[#001871]">
                      3. Data Processing
                    </h3>
                    <p className="mb-4">
                      We process your data in accordance with GDPR requirements. This includes:
                    </p>
                    <ul className="list-inside list-disc space-y-2 pl-4">
                      <li>Lawful, fair, and transparent processing</li>
                      <li>Purpose limitation and data minimization</li>
                      <li>Accuracy and storage limitation</li>
                      <li>Integrity and confidentiality</li>
                      <li>Accountability and documentation</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="mb-4 text-xl font-semibold text-[#001871]">
                      4. Your Rights Under GDPR
                    </h3>
                    <p className="mb-4">
                      You have the following rights regarding your personal data:
                    </p>
                    <ul className="list-inside list-disc space-y-2 pl-4">
                      <li>Right to access your data</li>
                      <li>Right to rectification of inaccurate data</li>
                      <li>Right to erasure ('right to be forgotten')</li>
                      <li>Right to restrict processing</li>
                      <li>Right to data portability</li>
                      <li>Right to object to processing</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="mb-4 text-xl font-semibold text-[#001871]">5. Data Security</h3>
                    <p className="mb-4">
                      We implement appropriate technical and organizational measures to ensure data
                      security, including:
                    </p>
                    <ul className="list-inside list-disc space-y-2 pl-4">
                      <li>Encryption of personal data</li>
                      <li>Regular security assessments</li>
                      <li>Access controls and authentication</li>
                      <li>Regular backups and disaster recovery</li>
                      <li>Staff training on data protection</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="mb-4 text-xl font-semibold text-[#001871]">
                      6. Intellectual Property
                    </h3>
                    <p className="mb-4">
                      All content and functionality within SOTA Navigator is protected by
                      intellectual property rights. Users may not copy, modify, or distribute our
                      content without permission.
                    </p>
                  </div>

                  <div>
                    <h3 className="mb-4 text-xl font-semibold text-[#001871]">
                      7. Updates to Terms
                    </h3>
                    <p className="mb-4">
                      We may update these terms periodically. We will notify you of any material
                      changes and obtain consent where required by law.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="relative bg-[#001871] py-24 text-white">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mx-auto max-w-3xl text-center"
            >
              <h2 className="mb-8 text-4xl font-bold [text-shadow:0_1px_2px_rgb(0_0_0/20%)]">
                Questions About Privacy?
              </h2>
              <p className="mb-8 text-xl text-white/90">
                Our Data Protection Officer is here to help. Contact us for any privacy-related
                questions or to exercise your data rights.
              </p>
              <p className="text-lg text-white/90">📧 privacy@sotanavigator.com</p>
            </motion.div>
          </div>
        </section>
      </PageLayout>
    </>
  )
}

export default Index
